import { handleFetchResponse } from '@noths/polaris-client-utils';
import { StatusCode } from '@noths/polaris-dev-ts-types';
import { createAsyncThunk } from '@reduxjs/toolkit';
import fetch from 'isomorphic-fetch';
import getConfig from 'next/config';

import { ReduxApplicationState } from 'src/redux/combinedReducer';
import { NextConfig } from 'src/types';
import { UserMarketingPreferenceResult } from '../hooks';
import { EnquiriesAPIPostData, PartnerStorefrontState } from '../types';

const { publicRuntimeConfig } = getConfig() as NextConfig;

export interface ContactPartnerSubmitPayload {
  fullName: string;
  email: string;
  message: string;
  marketingOptIn?: boolean;
  userAttributes: UserMarketingPreferenceResult['userAttributes'] | null;
}

export type MarketingPreferencesSubmitPayload = Pick<ContactPartnerSubmitPayload, 'email'>;

export const contactPartnerSubmit = createAsyncThunk<
  null,
  ContactPartnerSubmitPayload,
  { rejectValue: PartnerStorefrontState['error'] }
>(
  'partnerStorefront/contactPartnerSubmit',
  async (
    { fullName, email, message, marketingOptIn, userAttributes },
    { getState, dispatch, rejectWithValue },
  ) => {
    const { product } = getState() as ReduxApplicationState;
    const enquiriesUrl = `${publicRuntimeConfig.endpoints.enquiries}${product.code}/web_enquiries`;

    const postData: EnquiriesAPIPostData = {
      name: fullName,
      email,
      message,
    };

    if (userAttributes !== null) {
      const { email: emailFromAccount, name, family_name } = userAttributes;

      if (emailFromAccount) {
        postData.email = emailFromAccount!;
      }

      if (name && family_name) {
        postData.name = `${name} ${family_name}`;
      }
    }

    const apiResponse = await fetch(enquiriesUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(postData),
    });

    if ([StatusCode.UnprocessableEntity, StatusCode.TooManyRequests].includes(apiResponse.status)) {
      const { errors } = await apiResponse.json();

      return rejectWithValue(errors);
    }

    const result = await handleFetchResponse(apiResponse);

    if (marketingOptIn) {
      dispatch(submitMarketingPreferences({ email: postData.email }));
    }

    return result;
  },
);

export const submitMarketingPreferences = createAsyncThunk<null, MarketingPreferencesSubmitPayload>(
  'partnerStorefront/submitMarketingPreferences',
  async ({ email }) => {
    const marketingPreferencesAnonymousUrl = `${publicRuntimeConfig.hosts.communicationPreferences}/anonymous`;

    return await fetch(marketingPreferencesAnonymousUrl, {
      method: 'POST',
      body: JSON.stringify({
        address: email,
        channel: 'email',
        status: 'ENABLED',
        source: 'ASK_SELLER',
      }),
    }).then(handleFetchResponse);
  },
);
